/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useState, useLayoutEffect, useRef } from 'react';
import { QSTypography } from '../../../../../../shared/components/atoms';
import QSTag from '../../../../../../shared/components/atoms/tag';
import { ConversationTag } from '../../../typings';
import QSTooltip from '../../../../../../shared/components/molecules/tooltip';
import { Box } from '@mui/material';

export interface ChatTagsProps {
  tags: ConversationTag[];
}

const ChatTags = memo(
  ({ tags }: ChatTagsProps) => {
    const containerRef = useRef(null);
    const [visibleCount, setVisibleCount] = useState(tags.length);
    useLayoutEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = (container as HTMLElement).offsetWidth - 58;
      const gap = 6;
      let currentWidth = 0;
      let count = 0;

      const tagElements = (container as HTMLElement).children;
      for (let i = 0; i < tagElements.length; i++) {
        const tagWidth = (tagElements[i] as HTMLElement).offsetWidth;
        currentWidth += tagWidth + (i > 0 ? gap : 0);

        if (currentWidth > containerWidth) {
          (container as HTMLElement).style.width = `${currentWidth - tagWidth + 58}px`;
          break;
        }
        count++;
      }
      setVisibleCount(count);
    }, [tags]);

    if (!tags.length) {
      return null;
    }

    return (
      <div
        css={css`
          display: flex;
          align-items: start;
        `}
      >
        <div
          css={css`
            width: 250px;
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 6px;
            align-items: center;
            overflow: hidden;
            padding-left: 58px;
            height: 26px;
          `}
          ref={containerRef}
        >
          {tags.map((tag, index) => (
            <QSTypography
              key={index}
              css={css`
                display: inline-block;
                min-width: max-content;
                font-size: 12px;
                font-weight: 400;
                white-space: nowrap;
                color: ${tag.fontColorCode};
                background: ${tag.bgColorCode};
                padding: 4px 6px;
                border-radius: 4px;
              `}
            >
              {tag.name}
            </QSTypography>
          ))}
        </div>
        {visibleCount < tags.length && (
          <QSTooltip
            placement="bottom"
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  alignContent: 'flex-start',
                  flexWrap: 'wrap',
                  gap: '6px',
                  maxWidth: 220,
                  maxHeight: 'calc(3 * (26px + 4px))',
                  overflowY: 'auto',
                  padding: '4px',
                  '&::-webkit-scrollbar': { display: 'none' },
                  '&::-webkit-scrollbar-track': { display: 'none' },
                  '&::-webkit-scrollbar-thumb': { display: 'none' },
                }}
              >
                {tags.slice(visibleCount).map((tag, index) => (
                  <QSTag
                    key={tag.id ?? index}
                    tagFontColorCode={tag.fontColorCode}
                    tagBgColorCode={tag.bgColorCode}
                    tag={tag.name}
                  />
                ))}
              </Box>
            }
          >
            <Box>
              <QSTypography
                key={tags.length - visibleCount}
                css={css`
                  display: inline-block;
                  min-width: max-content;
                  font-size: 12px;
                  font-weight: 400;
                  white-space: nowrap;
                  color: rgba(122, 122, 122, 1);
                  background: rgba(122, 122, 122, 0.1);
                  padding: 4px 6px;
                  border-radius: 4px;
                `}
              >
                +{tags.length - visibleCount}
              </QSTypography>
            </Box>
          </QSTooltip>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.tags === nextProps.tags;
  }
);

export default ChatTags;
